import * as KotOR from "@/apps/game/KotOR";
import { Launcher } from "@/apps/launcher/context/Launcher";
import { ApplicationEnvironment } from "@/enums/ApplicationEnvironment";
import { GameInitializer } from "@/apps/game/GameInitializer";
import { applyProfileSeo } from "@/apps/common/seo/applyProfileSeo";
import { buildProfileSeo } from "@/apps/common/seo/profileSeo";

export class AppState {
  static eulaAccepted: boolean = false;
  static directoryLocated: boolean = false;
  static gameKey: KotOR.GameEngineType = KotOR.GameEngineType.KOTOR;
  static appProfile: any;
  static env: ApplicationEnvironment;
  static statsMode: number|undefined = undefined;

  /**
   * getProfile
   * Seeds Profiles.* from built-in launcher definitions when IndexedDB has never seen the launcher
   * (direct navigation to game.html?key=kotor, etc.).
   */
  static async getProfile(){
    const query = new URLSearchParams(window.location.search);
    await KotOR.ConfigClient.Init();
    await Launcher.InitProfiles();
    const rawKey = query.get("key");
    const gameKeys = ['kotor', 'tsl'];
    const key =
      rawKey && gameKeys.includes(rawKey) ? rawKey : "kotor";
    return Launcher.GetProfileByKey(key);
  }

  /**
   * initApp
   */
  static async initApp(){
    if(window.location.origin === 'file://'){
      AppState.env = ApplicationEnvironment.ELECTRON;
    }else{
      AppState.env = ApplicationEnvironment.BROWSER;
    }

    AppState.appProfile = await AppState.getProfile();
    if (!AppState.appProfile) {
      throw new Error('Failed to load game profile');
    }
    KotOR.ApplicationProfile.SetProfile(AppState.appProfile);
    KotOR.ApplicationProfile.InitEnvironment();

    applyProfileSeo(buildProfileSeo(AppState.appProfile, {
      appPath: '/game/',
      profileKey: AppState.appProfile.key || 'kotor',
    }));

    AppState.gameKey = KotOR.ApplicationProfile.GameKey;

    const eulaState: any = Object.assign({}, JSON.parse(window.localStorage.getItem('acceptEULA') as string));
    const gameEULAConfig = Object.assign({
      key: AppState.gameKey,
      version: null,
      date: null,
      accepted: false
    }, eulaState[AppState.gameKey]);
    eulaState[AppState.gameKey] = gameEULAConfig;
    AppState.eulaAccepted = !!gameEULAConfig.accepted;
    window.localStorage.setItem('acceptEULA', JSON.stringify(eulaState));

    console.log('gameEULAConfig', gameEULAConfig);
    console.log('eulaState', eulaState);
    AppState.directoryLocated = await AppState.checkGameDirectory();
    AppState.processEventListener('on-ready', [AppState.eulaAccepted]);
    // Only start loading once EULA is accepted and a game directory is available.
    // Otherwise leave setup modals interactive (loader would cover them).
    if(AppState.eulaAccepted && AppState.directoryLocated && !AppState.shouldDeferForAudioUnlock()){
      await AppState.loadGameDirectory();
    }else{
      AppState.loaderHide();
    }
  }

  /**
   * Returns true when the browser needs a user gesture before loading can start.
   */
  static shouldDeferForAudioUnlock(): boolean {
    return AppState.directoryLocated && AppState.needsBrowserAudioUnlock();
  }

  /**
   * Probe whether the browser will block audio until the user interacts.
   */
  static needsBrowserAudioUnlock(): boolean {
    if(AppState.env !== ApplicationEnvironment.BROWSER){
      return false;
    }
    try {
      const AudioContextCtor = window.AudioContext || (window as any).webkitAudioContext;
      if(!AudioContextCtor){
        return false;
      }
      const probe = new AudioContextCtor();
      const suspended = probe.state === 'suspended';
      void probe.close();
      return suspended;
    }catch{
      return false;
    }
  }

  /**
   * acceptEULA
   */
  static async acceptEULA(){
    AppState.eulaAccepted = true;
    if(!AppState.directoryLocated || AppState.shouldDeferForAudioUnlock()){
      AppState.loaderHide();
      AppState.processEventListener('on-preload', []);
      return;
    }
    await AppState.loadGameDirectory();
    AppState.processEventListener('on-preload', []);
  }

  /**
   * beginAfterAudioUnlock
   */
  static async beginAfterAudioUnlock(){
    await AppState.unlockBrowserAudio();
    await AppState.loadGameDirectory();
    AppState.processEventListener('on-preload', []);
  }

  static async unlockBrowserAudio(){
    if(AppState.env !== ApplicationEnvironment.BROWSER){
      return;
    }
    const audioCtx = KotOR.AudioEngine.GetAudioEngine().audioCtx;
    if(audioCtx.state === 'suspended'){
      await audioCtx.resume();
    }
  }

  /**
   * loadGameDirectory
   * - Used for Electron and Browser
   */
  static async loadGameDirectory(){
    AppState.loaderShow();
    GameInitializer.SetLoadingMessage('Locating Game Directory...');
  
    if(AppState.env == ApplicationEnvironment.ELECTRON){
      if(await KotOR.GameFileSystem.exists('chitin.key')){
        AppState.directoryLocated = true;
        AppState.processEventListener('on-preload', []);
        AppState.beginGame();
        return;
      }
      alert('Unable to locate chitin.key in the selected directory. Please try again.');
    }else{
      if(KotOR.ApplicationProfile.directoryHandle){
        const validated = await AppState.validateDirectoryHandle(KotOR.ApplicationProfile.directoryHandle);
        if(validated && await KotOR.GameFileSystem.exists('chitin.key')){
          AppState.directoryLocated = true;
          AppState.processEventListener('on-preload', []);
          AppState.beginGame();
          return;
        }
        alert('Unable to locate chitin.key in the selected directory. Please try again.');
      }
    }
    AppState.directoryLocated = false;
    AppState.loaderHide();
    AppState.processEventListener('on-preload', []);
  }

  /**
   * checkGameDirectory
   * - Used for Electron and Browser
   */
  static async checkGameDirectory(){
    if(AppState.env == ApplicationEnvironment.ELECTRON){
      if(await KotOR.GameFileSystem.exists('chitin.key')){
        return true;
      }
    }else{
      if(KotOR.ApplicationProfile.directoryHandle){
        const validated = await AppState.validateDirectoryHandle(KotOR.ApplicationProfile.directoryHandle);
        if(validated && await KotOR.GameFileSystem.exists('chitin.key')){
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Initializes the loading screen
   */
  static loaderInit(backgroundURL: string, logoURL: string): void {
    AppState.processEventListener('on-loader-init', [backgroundURL, logoURL]);
  }

  /**
   * Shows the loading screen
   */
  static loaderShow(){
    AppState.processEventListener('on-loader-show', []);
  }

  /**
   * Hides the loading screen
   */
  static loaderHide(){
    AppState.processEventListener('on-loader-hide', []);
  }

  /**
   * Sets the loading screen message
   */
  static loaderMessage(message: string): void {
    AppState.processEventListener('on-loader-message', [message]);
  }

  /**
   * beginGame
   */
  static async beginGame(){
    KotOR.ApplicationProfile.ENV = AppState.env;
    if(AppState.env == ApplicationEnvironment.ELECTRON){
      KotOR.ApplicationProfile.directory = AppState.appProfile.directory;
    }else{
      KotOR.ApplicationProfile.directoryHandle =
        AppState.appProfile.directory_handle ?? KotOR.ApplicationProfile.directoryHandle;
    }
    console.log('loading game...');
    AppState.loaderInit(AppState.appProfile.background, AppState.appProfile.logo);
    AppState.loaderShow();
    KotOR.GameState.GameKey = AppState.gameKey;
    KotOR.TextureLoader.GameKey = KotOR.GameState.GameKey;
    GameInitializer.AddEventListener('on-loader-message', (message: string) => {
      AppState.loaderMessage(message);
    });
    GameInitializer.AddEventListener('on-loader-show', () => {
      AppState.loaderShow();
    });
    GameInitializer.AddEventListener('on-loader-hide', () => {
      AppState.loaderHide();
    });

    await GameInitializer.Init(AppState.gameKey);

    console.log('loaded')
    KotOR.GUIListBox.InitTextures();
    KotOR.OdysseyWalkMesh.Init();
    KotOR.GameState.setDOMElement(document.getElementById('renderer-container') as HTMLElement);

    window.addEventListener('blur', (e) => {
      KotOR.AudioEngine.OnWindowFocusChange(false);
    });

    window.addEventListener('focus', (e) => {
      KotOR.AudioEngine.OnWindowFocusChange(true);
    });

    AppState.processEventListener('on-game-loaded', []);
    
    AppState.loaderMessage('GameState: Initializing...');
    await KotOR.GameState.Init();
    document.body.append(KotOR.GameState.stats.domElement);
    console.log('init complete');
    AppState.loaderHide();
  }

  /**
   * attachDirectoryPath
   * - Used for Electron
   */
  static attachDirectoryPath(path: string){
    KotOR.ConfigClient.set(`Profiles.${AppState.appProfile.key}.directory`, path);
    AppState.appProfile.directory = path;
    AppState.directoryLocated = true;
    AppState.loadGameDirectory();
  }

  /**
   * attachDirectoryHandle
   * - Used for Browser
   */
  static async attachDirectoryHandle(handle: FileSystemDirectoryHandle){
    KotOR.ApplicationProfile.directoryHandle = handle;
    AppState.appProfile.directory_handle = handle;
    KotOR.ConfigClient.set(`Profiles.${AppState.appProfile.key}.directory_handle`, handle);
    AppState.directoryLocated = true;
    if(AppState.shouldDeferForAudioUnlock()){
      AppState.processEventListener('on-preload', []);
      return;
    }
    await AppState.loadGameDirectory();
  }

  /**
   * validateDirectoryHandle
   * - Used for Browser
   */
  static async validateDirectoryHandle(handle: FileSystemDirectoryHandle){
    try{
      if ((await handle.requestPermission({ mode: 'readwrite' })) === 'granted') {
        return true;
      }
      return false;
    }catch(e){
      console.error(e);
      return false;
    }
  }

  static consoleCommand(command: string){
    console.log('consoleCommand', command);
    KotOR.GameState.CheatConsoleManager.processCommand(command);
  }

  static togglePerformanceMonitor(){
    let mode: number|undefined = AppState.statsMode;
    if(mode == undefined){
      mode = 0;
    }else{
      mode++;
    }
    if(mode > 2){
      mode = undefined;
    }
    AppState.statsMode = mode;
    KotOR.GameState.stats.showPanel(mode as any);
  }

  static toggleDebugger(){
    KotOR.GameState.Debugger.open();
  }

  static reloadLastSave(){
    const gameKey = KotOR.GameState.GameKey;
    const lastSaveId = parseInt(localStorage.getItem(`${gameKey}_last_save_id`) || '-1');
    const saveGame = KotOR.SaveGame.saves[lastSaveId];
    if(!saveGame){ return; }
    saveGame.load();
  }

  /**
   * Event Listeners
   */

  static #eventListeners: any = {};

  static addEventListener<T>(type: T, cb: Function): void {
    if(!Array.isArray(this.#eventListeners[type])){
      this.#eventListeners[type] = [];
    }
    if(Array.isArray(this.#eventListeners[type])){
      let ev = this.#eventListeners[type];
      let index = ev.indexOf(cb);
      if(index == -1){
        ev.push(cb);
      }else{
        console.warn('Event Listener: Already added', type);
      }
    }else{
      console.warn('Event Listener: Unsupported', type);
    }
  }

  static removeEventListener<T>(type: T, cb: Function): void {
    if(Array.isArray(this.#eventListeners[type])){
      let ev = this.#eventListeners[type];
      let index = ev.indexOf(cb);
      if(index >= 0){
        ev.splice(index, 1);
      }else{
        console.warn('Event Listener: Already removed', type);
      }
    }else{
      console.warn('Event Listener: Unsupported', type);
    }
  }

  static processEventListener<T>(type: T, args: any[] = []): void {
    if(Array.isArray(this.#eventListeners[type])){
      let ev = this.#eventListeners[type];
      for(let i = 0; i < ev.length; i++){
        const callback = ev[i];
        if(typeof callback === 'function'){
          callback(...args);
        }
      }
    }else{
      console.warn('Event Listener: Unsupported', type);
    }
  }

}
